import {useMemo, type ReactNode} from 'react';
import {
  ChevronRight,
  Contact,
  FileText,
  IdCard,
  QrCode,
  ScanLine,
  UserRound,
} from 'lucide-react';
import {
  AssistantPanel,
  StepProgress,
  Tooltip,
  cn,
  useFitToHeight,
  useModalControls,
} from '@chekinapp/ui';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ShareRegistrationLinkSheet} from 'components/ModalSheets/ShareRegistrationLink';
import {TreatImagesInfoSheet} from 'pages/IdentityVerification/TreatImagesInfoModal';
import {usePath, useAvailableLocks, usePaymentsEntryPath} from 'hooks';
import {PATHS} from 'routes/constants';
import {CHAT_HOST_CARD_ID, SHARE_LINK_CARD_ID} from '../rules/registration.rules';
import {CHAT_FAQ_CARD_ID, CHAT_GUIDEBOOKS_CARD_ID} from '../rules/chat.rules';
import {
  DASHED_ISNT_A_CHARGE_CARD_ID,
  WHATS_AN_AUTHORISATION_CARD_ID,
  WHATS_A_HOLD_CARD_ID,
} from '../rules/payments.rules';
import {HoldInfoModal} from 'pages/Payments/components';
import {KEYS_LAST_STEP_CARD_ID} from '../rules/remoteAccess.rules';
import {
  getConditionRows,
  resolveConditionPath,
} from 'components/RemoteAccess/lockConditions';
import {type RegistrationStepKey} from 'components/RegistrationProgress';
import {POSTHOG_EVENTS, useAnalytics} from 'analytics';
import {VelaScene, type VelaCardSection} from '../engine/types';
import {type VelaResult} from '../hooks/useVela';
import {VelaChat} from './VelaChat';
import {VelaChatContext} from './VelaChatContext';
import {VelaGuidebookNav} from './VelaGuidebookNav';
import {VelaHeader} from './VelaHeader';
import {VelaInviteCard} from './VelaInviteCard';
import {VelaPhoneHandoff} from './VelaPhoneHandoff';
import {VelaTip} from './VelaTip';
import {VelaTripGlance} from './VelaTripGlance';
import {VelaTripProvenance} from './VelaTripProvenance';
import {useIsPhoneHandoffAvailable} from '../hooks/useIsPhoneHandoffAvailable';
import {fitHiddenClass, fitQrFirst, getVelaFitPlan} from '../fit/velaFit';
import {useHomeGuidebooksPath} from 'hooks/guidebooks/useHomeGuidebooksPath';

const STEP_ICON: Record<RegistrationStepKey, ReactNode> = {
  presence: <QrCode />,
  contacts: <Contact />,
  documentSelection: <IdCard />,
  document: <ScanLine />,
  selfie: <UserRound />,
  form: <FileText />,
};

const STEP_LABEL: Record<RegistrationStepKey, string> = {
  presence: 'step.presence',
  contacts: 'step.contacts',
  documentSelection: 'step.document_selection',
  document: 'step.document',
  selfie: 'step.selfie',
  form: 'step.form',
};

const WHY_PHOTOS_CARD_ID = 'why-photos';

const PANEL_FIT_CLASS = cn(
  "[&[data-fit~='2']_[data-slot=vela-intro]]:mt-2",
  "[&[data-fit~='3']_[data-slot=vela-intro]]:hidden",
);
const PANEL_SCROLL_FALLBACK_CLASS = '[&[data-fit-overflow]]:overflow-y-auto';
const ROW_FIT_CLASS = "[[data-fit~='1']_&]:py-2";
const PHOTO_TIP_COMPACT_CLASS = cn(
  "[[data-fit~='4']_&]:items-center [[data-fit~='4']_&]:gap-1.5",
  "[[data-fit~='4']_&>span:first-child]:size-[26px]",
  "[[data-fit~='4']_&_strong+span]:hidden",
);
const PHOTO_TIP_STAGGER_MS = 60;

type Props = {
  vela: VelaResult;
  stickyHeader?: boolean;
  fitEnabled?: boolean;
  className?: string;
  id?: string;
};

function VelaPanelBody({
  vela,
  stickyHeader = false,
  fitEnabled = false,
  className,
  id,
}: Props) {
  const {t} = useTranslation('vela');
  const {scene, header, progress, content} = vela;
  const isPhoneHandoffAvailable = useIsPhoneHandoffAvailable(scene);
  const {track} = useAnalytics();
  const fitPlan = useMemo(
    () => getVelaFitPlan(scene, content.sections),
    [scene, content.sections],
  );
  const fit = useFitToHeight<HTMLElement>({
    levels: fitPlan.levels,
    enabled: fitEnabled,
    onFit: ({element}) => fitQrFirst(element, fitPlan.levels),
  });
  const whyPhotos = useModalControls();
  const shareLink = useModalControls();
  const holdInfo = useModalControls();
  const authorisationInfo = useModalControls();
  const dashedInfo = useModalControls();
  const navigate = useNavigate();
  const {getFullPath, getMainPath} = usePath();
  const paymentsEntryPath = usePaymentsEntryPath();
  const homeGuidebooksPath = useHomeGuidebooksPath();
  const {lockConditions} = useAvailableLocks();
  const blockingKeyCondition = useMemo(
    () => getConditionRows(lockConditions?.incomplete_conditions).blocking,
    [lockConditions?.incomplete_conditions],
  );

  const openHelp = (cardId: string, openModal: () => void) => {
    track(POSTHOG_EVENTS.HELP_VIEWED, {scene, card_id: cardId});
    openModal();
  };

  const cardActions: Record<string, () => void> = {
    [WHY_PHOTOS_CARD_ID]: () => openHelp(WHY_PHOTOS_CARD_ID, whyPhotos.openModal),
    [SHARE_LINK_CARD_ID]: () => openHelp(SHARE_LINK_CARD_ID, shareLink.openModal),
    [WHATS_A_HOLD_CARD_ID]: () => openHelp(WHATS_A_HOLD_CARD_ID, holdInfo.openModal),
    [WHATS_AN_AUTHORISATION_CARD_ID]: () =>
      openHelp(WHATS_AN_AUTHORISATION_CARD_ID, authorisationInfo.openModal),
    [DASHED_ISNT_A_CHARGE_CARD_ID]: () =>
      openHelp(DASHED_ISNT_A_CHARGE_CARD_ID, dashedInfo.openModal),
    [CHAT_HOST_CARD_ID]: () => {
      track(POSTHOG_EVENTS.HELP_VIEWED, {scene, card_id: CHAT_HOST_CARD_ID});
      navigate(getFullPath(PATHS.chat));
    },
    [CHAT_FAQ_CARD_ID]: () => {
      track(POSTHOG_EVENTS.HELP_VIEWED, {scene, card_id: CHAT_FAQ_CARD_ID});
      navigate(getFullPath(PATHS.faq));
    },
    [CHAT_GUIDEBOOKS_CARD_ID]: () => {
      track(POSTHOG_EVENTS.HELP_VIEWED, {scene, card_id: CHAT_GUIDEBOOKS_CARD_ID});
      navigate(homeGuidebooksPath);
    },
    ...(blockingKeyCondition && {
      [KEYS_LAST_STEP_CARD_ID]: () => {
        track(POSTHOG_EVENTS.HELP_VIEWED, {scene, card_id: KEYS_LAST_STEP_CARD_ID});
        navigate(
          resolveConditionPath(blockingKeyCondition.kind, {
            getFullPath,
            getMainPath,
            paymentsEntryPath,
          }),
        );
      },
    }),
  };

  const handleAsk = (cardId: string) => {
    track(POSTHOG_EVENTS.HELP_VIEWED, {scene, card_id: cardId});
  };

  const renderSection = (section: VelaCardSection) => {
    const sectionClassName = fitHiddenClass(fitPlan.sectionDropLevels.get(section.id));

    if (section.id === 'askVela') {
      return (
        <AssistantPanel.Section
          key={section.id}
          label={section.label}
          className={sectionClassName}
        >
          <VelaChat
            key={scene}
            questions={section.cards.map(card => ({
              id: card.id,
              question: card.title,
              answer: card.description,
              chipClassName: fitHiddenClass(fitPlan.cardDropLevels.get(card.id)),
            }))}
            onAsk={handleAsk}
          />
        </AssistantPanel.Section>
      );
    }

    if (section.id === 'nextGuest') {
      return (
        <AssistantPanel.Section
          key={section.id}
          label={section.label}
          className={sectionClassName}
        >
          {section.cards.map(card => (
            <VelaInviteCard
              key={card.id}
              title={card.title}
              description={card.description}
              onCopy={() =>
                track(POSTHOG_EVENTS.REGISTRATION_LINK_COPIED, {
                  scene,
                  source: 'vela_invite',
                })
              }
              onShare={() => {
                track(POSTHOG_EVENTS.REGISTRATION_LINK_SHARE_OPENED, {
                  scene,
                  source: 'vela_invite',
                });
                shareLink.openModal();
              }}
            />
          ))}
        </AssistantPanel.Section>
      );
    }

    if (section.id === 'quickActions') {
      return (
        <AssistantPanel.Section
          key={section.id}
          label={section.label}
          className={sectionClassName}
        >
          <AssistantPanel.Actions>
            {section.cards.map(card => (
              <AssistantPanel.Action
                key={card.id}
                title={card.title}
                description={card.description}
                onClick={cardActions[card.id]}
              />
            ))}
          </AssistantPanel.Actions>
        </AssistantPanel.Section>
      );
    }

    if (section.id === 'photoTips') {
      return (
        <AssistantPanel.Section
          key={section.id}
          label={section.label}
          className={cn('mb-[11px] gap-[9px]', sectionClassName)}
        >
          {section.cards.map((card, index) => (
            <VelaTip
              key={card.id}
              className={cn(
                'gap-2.5 py-0 duration-[400ms] animate-in fade-in-0 slide-in-from-bottom-1 fill-mode-both motion-reduce:animate-none',
                PHOTO_TIP_COMPACT_CLASS,
              )}
              style={{animationDelay: `${index * PHOTO_TIP_STAGGER_MS}ms`}}
              icon={card.icon}
              title={card.title}
              description={card.description}
            />
          ))}
        </AssistantPanel.Section>
      );
    }

    return (
      <AssistantPanel.Section
        key={section.id}
        label={section.label}
        className={sectionClassName}
      >
        {section.cards.map((card, index) => {
          const onClick = cardActions[card.id];
          const isFirst = index === 0;
          const dropClassName = fitHiddenClass(fitPlan.cardDropLevels.get(card.id));

          if (onClick) {
            return (
              <button
                key={card.id}
                type="button"
                onClick={onClick}
                data-pri={card.priority}
                className={cn(
                  'group -mx-2 flex w-[calc(100%+16px)] items-center gap-1 rounded-[10px] px-2 text-left transition-colors hover:bg-[var(--assistant-tip-icon-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--assistant-section-accent)]',
                  isFirst && '-mt-[10px]',
                  dropClassName,
                )}
              >
                <AssistantPanel.Tip
                  className={cn('flex-1 [&_strong]:leading-[18px]', ROW_FIT_CLASS)}
                  title={card.title}
                  description={card.description}
                />
                <ChevronRight className="h-4 w-4 flex-none text-[var(--assistant-tip-description)] transition-transform group-hover:translate-x-0.5" />
              </button>
            );
          }

          return (
            <AssistantPanel.Tip
              key={card.id}
              className={cn('py-2', isFirst && 'pt-px', dropClassName)}
              title={card.title}
              description={card.description}
              tone={card.tone}
            />
          );
        })}
      </AssistantPanel.Section>
    );
  };

  if (scene === VelaScene.GuidebookDetail) {
    return <VelaGuidebookNav id={id} header={header} className={className} />;
  }

  const isChat = scene === VelaScene.Chat;
  const isQuickActions = (section: VelaCardSection) => section.id === 'quickActions';
  const quickActionSections = isChat ? content.sections.filter(isQuickActions) : [];
  const otherSections = isChat
    ? content.sections.filter(section => !isQuickActions(section))
    : [];

  return (
    <AssistantPanel
      ref={fit.ref}
      id={id}
      className={cn(
        stickyHeader && 'pt-0',
        PANEL_FIT_CLASS,
        PANEL_SCROLL_FALLBACK_CLASS,
        className,
      )}
    >
      <VelaHeader
        name={header.name}
        status={header.status}
        intro={progress ? undefined : header.intro}
        className={cn(
          stickyHeader &&
            'sticky top-0 z-[5] -mx-6 bg-[var(--assistant-panel-bg)] px-6 pt-[26px] backdrop-blur-xl',
        )}
      />

      {progress && (
        <AssistantPanel.Section className="mt-4">
          <AssistantPanel.Tip
            className="py-0"
            title={progress.title}
            description={progress.intro}
          />
          <StepProgress
            className="mb-[11px] mt-[14px]"
            size="lg"
            animateAdvance
            steps={progress.stepKeys.map((key, index) => {
              const label = t('step.tooltip', {n: index + 1, label: t(STEP_LABEL[key])});
              return {label, icon: <StepIcon stepKey={key} label={label} />};
            })}
            activeIndex={progress.activeIndex}
            skippedIndices={progress.skippedIndices}
          />
        </AssistantPanel.Section>
      )}

      {isChat ? (
        <>
          {quickActionSections.map(renderSection)}
          <VelaChatContext />
          {otherSections.map(renderSection)}
        </>
      ) : (
        content.sections.map(renderSection)
      )}

      {scene === VelaScene.TravelGuide && (
        <>
          <VelaTripGlance />
          <VelaTripProvenance />
        </>
      )}

      {isPhoneHandoffAvailable && (
        <AssistantPanel.Section label={t('section.no_camera')}>
          <VelaPhoneHandoff />
        </AssistantPanel.Section>
      )}

      <TreatImagesInfoSheet open={whyPhotos.isOpen} onClose={whyPhotos.closeModal} />
      <ShareRegistrationLinkSheet
        open={shareLink.isOpen}
        onClose={shareLink.closeModal}
      />
      <HoldInfoModal isOpen={holdInfo.isOpen} close={holdInfo.closeModal} />
      <HoldInfoModal
        variant="authorisation"
        isOpen={authorisationInfo.isOpen}
        close={authorisationInfo.closeModal}
      />
      <HoldInfoModal
        variant="both"
        isOpen={dashedInfo.isOpen}
        close={dashedInfo.closeModal}
      />
    </AssistantPanel>
  );
}

function StepIcon({stepKey, label}: {stepKey: RegistrationStepKey; label: string}) {
  return (
    <Tooltip content={label}>
      <span className="grid size-full place-items-center">{STEP_ICON[stepKey]}</span>
    </Tooltip>
  );
}

export {VelaPanelBody};
